import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button as MuiButton,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Button } from '@orijinworks/frontend-commons';
import { PROGRAM_COURSE_FILTER_OPTIONS, PROGRAM_COURSE_STATUS } from '../../utils/program-review.utils';

const statusChipColor = {
  [PROGRAM_COURSE_STATUS.PENDING]: 'warning',
  [PROGRAM_COURSE_STATUS.LINKED]: 'success',
  [PROGRAM_COURSE_STATUS.NOT_ON_CANVAS]: 'default',
};

const statusLabel = {
  [PROGRAM_COURSE_STATUS.PENDING]: 'Pending',
  [PROGRAM_COURSE_STATUS.LINKED]: 'Linked',
  [PROGRAM_COURSE_STATUS.NOT_ON_CANVAS]: 'Not on Canvas',
};

const rowBg = {
  [PROGRAM_COURSE_STATUS.PENDING]: 'rgba(237, 108, 2, 0.04)',
  [PROGRAM_COURSE_STATUS.LINKED]: 'rgba(46, 125, 50, 0.04)',
  [PROGRAM_COURSE_STATUS.NOT_ON_CANVAS]: 'transparent',
};

const PROGRAM_COURSE_COLUMNS = [
  { label: 'D2L Course Name', minWidth: 220 },
  { label: 'Canvas Course Title', minWidth: 170 },
  { label: 'Programs', minWidth: 260 },
  { label: 'Canvas ID', minWidth: 170 },
  { label: 'Status', minWidth: 130 },
  { label: 'Action', minWidth: 140 },
];

/** Matches the shared DataTable header styling used by the course search view. */
const headerCellSx = {
  fontWeight: 700,
  color: 'text.secondary',
  backgroundColor: 'grey.50',
  whiteSpace: 'nowrap',
  fontSize: '0.75rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  borderBottom: '1px solid',
  borderColor: 'divider',
  py: 1.25,
};

const CoverageTile = ({ value, label, valueColor }) => (
  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
    <Box sx={{ px: 2.5, py: 2 }}>
      <Typography variant="h4" sx={valueColor ? { color: valueColor } : undefined}>
        {value}
      </Typography>
    </Box>
    <Box
      sx={{
        px: 2.5,
        py: 1,
        backgroundColor: 'grey.50',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Paper>
);

CoverageTile.propTypes = {
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  valueColor: PropTypes.string,
};

CoverageTile.defaultProps = {
  valueColor: undefined,
};

const SelectionBar = ({ selectedCount, bulkLinking, onLinkSelected, onClearSelection }) => {
  if (selectedCount === 0) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={2} data-testid="program-bulk-selection-callout">
      <Typography fontWeight={600}>
        {selectedCount} course{selectedCount === 1 ? '' : 's'} selected
      </Typography>
      <Box flex={1} />
      <Button
        color="primary"
        variant="contained"
        size="small"
        disabled={bulkLinking}
        onClick={onLinkSelected}
        data-testid="program-link-selected"
      >
        Link Selected
      </Button>
      <Box marginLeft={1}>
        <Button
          color="default"
          variant="outlined"
          size="small"
          disabled={bulkLinking}
          onClick={onClearSelection}
          data-testid="program-clear-selection"
        >
          Clear selection
        </Button>
      </Box>
    </Box>
  );
};

SelectionBar.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  bulkLinking: PropTypes.bool.isRequired,
  onLinkSelected: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
};

const FilterToolbar = ({ showFilters, statusFilter, onStatusFilterChange, canSelectAllPending, onSelectAllPending }) => {
  if (!showFilters && !canSelectAllPending) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
      {showFilters
        ? PROGRAM_COURSE_FILTER_OPTIONS.map((option) => (
            <Chip
              key={option.key}
              label={option.label}
              color={statusFilter === option.key ? 'primary' : 'default'}
              variant={statusFilter === option.key ? 'filled' : 'outlined'}
              onClick={() => onStatusFilterChange(option.key)}
              clickable
              sx={{ height: 32, fontSize: '0.875rem' }}
              data-testid={`program-filter-${option.key}`}
            />
          ))
        : null}
      <Box flex={1} />
      {canSelectAllPending ? (
        <Button
          color="primary"
          variant="outlined"
          size="small"
          onClick={onSelectAllPending}
          data-testid="select-all-pending"
        >
          Select All Pending
        </Button>
      ) : null}
    </Box>
  );
};

FilterToolbar.propTypes = {
  showFilters: PropTypes.bool.isRequired,
  statusFilter: PropTypes.string.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  canSelectAllPending: PropTypes.bool.isRequired,
  onSelectAllPending: PropTypes.func.isRequired,
};

const ProgramCourseListView = ({
  program,
  coverage,
  courses,
  loading,
  error,
  statusFilter,
  onStatusFilterChange,
  selectedIds,
  onToggleSelected,
  onSelectAllPending,
  onClearSelection,
  onLinkSelected,
  bulkLinking,
  canSelectAllPending,
  onBack,
  onResolve,
  selectedCount,
}) => {
  const visibleNotOnCanvasCount = courses.filter((course) => course.status === PROGRAM_COURSE_STATUS.NOT_ON_CANVAS).length;
  const showNotOnCanvasCallout = coverage.notOnCanvas > 0 && visibleNotOnCanvasCount > 0;
  /** Filters stay hidden while a selection is active so the bulk action never spans hidden rows. */
  const showFilters = selectedCount === 0;

  return (
    <Box data-testid="program-course-list-view">
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <MuiButton variant="text" onClick={onBack} data-testid="program-courses-back">
          ← Programs
        </MuiButton>
        <Typography color="text.secondary">/</Typography>
        <Typography fontWeight={600}>{program?.resourceName}</Typography>
      </Box>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={2} mb={2}>
        <CoverageTile value={coverage.total} label="Total Courses" />
        <CoverageTile value={coverage.pending} label="Pending" valueColor="warning.main" />
        <CoverageTile value={coverage.linked} label="Linked" valueColor="success.main" />
        <CoverageTile value={coverage.notOnCanvas} label="Not on Canvas" />
      </Box>

      <SelectionBar
        selectedCount={selectedCount}
        bulkLinking={bulkLinking}
        onLinkSelected={onLinkSelected}
        onClearSelection={onClearSelection}
      />

      <FilterToolbar
        showFilters={showFilters}
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        canSelectAllPending={canSelectAllPending}
        onSelectAllPending={onSelectAllPending}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : null}

      {error ? (
        <Typography color="error" data-testid="program-courses-error">
          {error}
        </Typography>
      ) : null}

      {!loading && !error ? (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ borderColor: 'divider', overflow: 'hidden', backgroundColor: 'common.white' }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" data-testid="program-courses-table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={headerCellSx} />
                  {PROGRAM_COURSE_COLUMNS.map((column) => (
                    <TableCell key={column.label} scope="col" sx={{ ...headerCellSx, minWidth: column.minWidth }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {courses.map((course) => {
                  const isPending = course.status === PROGRAM_COURSE_STATUS.PENDING;
                  return (
                    <TableRow
                      key={course.resourceId}
                      sx={{ backgroundColor: rowBg[course.status] }}
                      data-testid={`program-course-row-${course.resourceId}`}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          disabled={!isPending || course.catalogId == null}
                          checked={selectedIds.has(course.resourceId)}
                          onChange={() => onToggleSelected(course.resourceId, course.status)}
                          inputProps={{ 'aria-label': `Select ${course.resourceName}` }}
                        />
                      </TableCell>
                      <TableCell>{course.resourceName}</TableCell>
                      <TableCell>{course.canvasCourseTitle || '—'}</TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Box display="flex" flexDirection="column" gap={0.25}>
                          {(course.programs || []).map((programRef) => {
                            const isSelectedProgram =
                              program?.resourceId != null && String(programRef.resourceId) === String(program.resourceId);
                            return (
                              <Typography
                                key={programRef.resourceId}
                                variant="body2"
                                sx={{
                                  color: isSelectedProgram ? 'primary.main' : 'text.secondary',
                                  fontWeight: isSelectedProgram ? 600 : 400,
                                }}
                              >
                                {programRef.resourceName}
                              </Typography>
                            );
                          })}
                        </Box>
                      </TableCell>
                      <TableCell>{course.canvasExternalId || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusLabel[course.status] || course.status}
                          color={statusChipColor[course.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {isPending ? (
                          <Button
                            color="primary"
                            variant="outlined"
                            size="small"
                            onClick={() => onResolve(course)}
                            disabled={course.catalogId == null}
                            data-testid={`program-resolve-${course.resourceId}`}
                          >
                            Resolve
                          </Button>
                        ) : null}
                        {course.status === PROGRAM_COURSE_STATUS.LINKED ? (
                          <Typography variant="body2" color="text.secondary">
                            {course.linkedResourceName || course.resourceName}
                          </Typography>
                        ) : null}
                        {course.status === PROGRAM_COURSE_STATUS.NOT_ON_CANVAS ? (
                          <Typography variant="body2" color="text.secondary">
                            Awaiting Canvas event
                          </Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {courses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary">No courses in this view.</Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}

      {showNotOnCanvasCallout ? (
        <Alert
          severity="info"
          sx={{
            mt: 2,
            padding: "6px 16px !important",
            alignItems: 'flex-start',
            '& .MuiAlert-icon': { mr: 2, p: 0, mt: '2px' },
            '& .MuiAlert-message': { p: 0 },
          }}
          data-testid="not-on-canvas-callout"
        >
          <Typography fontWeight={600} gutterBottom>
            About Not on Canvas courses
          </Typography>
          <Typography variant="body2" color="text.secondary">
            These D2L courses belong to this program but have no Canvas course yet. No link action is available until Canvas
            sends a Live Event — at that point they will appear as Pending.
          </Typography>
        </Alert>
      ) : null}
    </Box>
  );
};

ProgramCourseListView.propTypes = {
  program: PropTypes.object,
  coverage: PropTypes.shape({
    total: PropTypes.number,
    pending: PropTypes.number,
    linked: PropTypes.number,
    notOnCanvas: PropTypes.number,
  }).isRequired,
  courses: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  statusFilter: PropTypes.string.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onToggleSelected: PropTypes.func.isRequired,
  onSelectAllPending: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  onLinkSelected: PropTypes.func.isRequired,
  bulkLinking: PropTypes.bool,
  canSelectAllPending: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
  selectedCount: PropTypes.number.isRequired,
};

ProgramCourseListView.defaultProps = {
  program: null,
  loading: false,
  error: null,
  bulkLinking: false,
  canSelectAllPending: true,
};

export default ProgramCourseListView;
